import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { Card } from "../shared/Card";
import { Button } from "../shared/Button";
import { Badge } from "../shared/Badge";
import IconTile from "../shared/IconTile";
import StatusRing from "../shared/StatusRing";
import { fullName, uid } from "../../lib/format";
import FamilyForm from "./FamilyForm";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

// ============================================================
// FamiliesPage — read-only for Pastor + Team Member; SA can create/edit/delete.
// Limited Admin can VIEW but not edit (matches sibling pages).
// Delete uses the server-side cascade endpoint which also clears familyId
// from every linked member record.
// ============================================================
async function apiCreateFamily(payload) {
  const res = await kcFetch(`${API_BASE}/families`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Create failed");
  return res.json();
}
async function apiUpdateFamily(id, patch) {
  const res = await kcFetch(`${API_BASE}/families/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Update failed");
  return res.json();
}
async function apiDeleteFamily(id) {
  const res = await kcFetch(`${API_BASE}/families/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Delete failed");
  return res.json();
}

export default function FamiliesPage({ isSuperAdmin, families, setFamilies, setMembers, members, addAudit, currentUser }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editFamily, setEditFamily] = useState(null);

  const saveFamily = async (data) => {
    try {
      if (data.id) {
        const fresh = await apiUpdateFamily(data.id, data);
        setFamilies(families.map(f => f.id === data.id ? fresh : f));
        // Cascade the new member list onto local member state so the family
        // card and member Church Info update immediately without a refetch.
        if (typeof setMembers === "function") {
          const newSet = new Set(fresh.members || []);
          setMembers(prev => prev.map(m => {
            if (newSet.has(m.id)) return m.familyId === fresh.id ? m : { ...m, familyId: fresh.id };
            if (m.familyId === fresh.id) return { ...m, familyId: null };
            return m;
          }));
        }
      } else {
        const draft = { ...data, id: uid() };
        const fresh = await apiCreateFamily(draft);
        setFamilies([...families, fresh]);
        if (typeof setMembers === "function" && Array.isArray(fresh.members) && fresh.members.length) {
          const newSet = new Set(fresh.members);
          setMembers(prev => prev.map(m => newSet.has(m.id) ? { ...m, familyId: fresh.id } : m));
        }
        addAudit && addAudit(currentUser.id, currentUser.name, "Created family", `Added ${fresh.name}`);
      }
      setShowModal(false); setEditFamily(null);
    } catch (err) {
      alert(`Save failed: ${err.message || err}`);
    }
  };

  const deleteFamily = async (id) => {
    const f = families.find(x => x.id === id);
    if (!window.confirm(`Delete ${f.name}? This will also unlink any members from this family.`)) return;
    try {
      const result = await apiDeleteFamily(id);
      setFamilies(families.filter(x => x.id !== id));
      // Reflect the cascade in the local member state so the UI updates
      // without needing a full reload.
      if (typeof setMembers === "function" && result.membersCleared > 0) {
        setMembers(prev => prev.map(m => m.familyId === id ? { ...m, familyId: null } : m));
      }
      addAudit && addAudit(currentUser.id, currentUser.name, "Deleted family", `Removed ${f.name} (unlinked ${result.membersCleared} member${result.membersCleared === 1 ? "" : "s"})`);
    } catch (err) {
      alert(`Delete failed: ${err.message || err}`);
    }
  };

  // RBAC: ONLY Super Admin can create/edit/delete. Everyone else (LA / Pastor /
  // Team Member) sees the page in read-only mode.
  const canEdit = !!isSuperAdmin;

  return (
    <div>
      {/* Page header — count + read-only badge on the left, single primary CTA on the right */}
      <div
        className="flex items-center justify-between mb-20"
        style={{ gap: "var(--space-md)", flexWrap: "wrap" }}
      >
        <div className="text-meta" style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-sm)" }}>
          <span data-testid="families-count">{families.length} families</span>
          {!canEdit && (
            <Badge variant="blue" data-testid="families-readonly-badge">
              <CustomIcon name="lock" size={10} style={{ marginRight: 4 }} /> Read-only
            </Badge>
          )}
        </div>
        {canEdit && (
          <Button
            variant="primary"
            data-testid="families-create-btn"
            onClick={() => { setEditFamily(null); setShowModal(true); }}
          >
            <CustomIcon name="add" size={12} /> Create Family
          </Button>
        )}
      </div>

      {/* Family cards grid — every card is variant="blue" per the design system.
          Semantic: blue = scheduled / assigned / grouped family unit. */}
      <div
        data-testid="families-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
          gap: "var(--space-md)",
        }}
      >
        {families.map(f => {
          const fm = members.filter(m => m.familyId === f.id);
          const head = members.find(m => m.id === f.head);
          return (
            <Card
              key={f.id}
              variant="blue"
              data-testid={`family-card-${f.id}`}
              style={{ padding: "var(--space-md)", display: "flex", flexDirection: "column", gap: "var(--space-sm)" }}
            >
              {/* Card header — family name + head + family icon */}
              <div className="flex items-center justify-between" style={{ gap: "var(--space-sm)" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="text-card-primary"
                    style={{ fontFamily: "'Cinzel',serif", textTransform: "uppercase", letterSpacing: "0.06em" }}
                  >
                    {f.name}
                  </div>
                  <div className="text-card-secondary">
                    Head:{" "}
                    {head ? (
                      <a
                        href={`/members/${head.id}`}
                        data-testid={`family-head-link-${f.id}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/members/${head.id}`); }}
                        className="kc-member-link"
                      >
                        {fullName(head)}
                      </a>
                    ) : "—"}
                  </div>
                  <div className="text-meta" style={{ marginTop: 2 }}>
                    {fm.length} {fm.length === 1 ? "member" : "members"}
                  </div>
                </div>
                <IconTile name="family" size={22} variant="blue" />
              </div>

              {/* Member list — StatusRing + Avatar + name + marital-status Badge */}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-sm)" }}>
                {fm.length === 0 ? (
                  <div className="text-card-secondary" data-testid={`family-empty-${f.id}`}>
                    No members linked
                  </div>
                ) : (
                  fm.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center"
                      style={{ gap: "var(--space-sm)" }}
                    >
                      <StatusRing status={m.memberStatus} viewerRole={currentUser?.role}>
                        <Avatar
                          name={fullName(m)}
                          photo={m.photo}
                          memberId={m.id}
                          status={m.memberStatus}
                          noRing
                          style={{ width: 26, height: 26, fontSize: 10 }}
                        />
                      </StatusRing>
                      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                        <a
                          href={`/members/${m.id}`}
                          data-testid={`family-member-link-${m.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/members/${m.id}`); }}
                          className="kc-member-link text-card-primary"
                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {fullName(m)}
                        </a>
                        <span className="text-card-secondary">{m.maritalStatus || m.memberStatus}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Edit / Delete controls — Super Admin only */}
              {canEdit && (
                <div className="flex" style={{ gap: "var(--space-sm)", marginTop: "var(--space-xs)" }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid={`family-edit-${f.id}`}
                    onClick={() => { setEditFamily(f); setShowModal(true); }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    data-testid={`family-delete-${f.id}`}
                    onClick={() => deleteFamily(f.id)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">{editFamily ? "Edit Family" : "New Family"}</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <FamilyForm family={editFamily} members={members} onSave={saveFamily} onClose={() => setShowModal(false)} />
          </div>
        </div>
      )}
      <style>{`
        .kc-member-link {
          color: var(--kc-accent);
          text-decoration: none;
          transition: text-decoration 0.1s, color 0.1s;
          cursor: pointer;
        }
        .kc-member-link:hover {
          text-decoration: underline;
          color: var(--kc-text);
        }
        .kc-member-link:focus-visible {
          outline: 2px solid var(--kc-accent);
          outline-offset: 2px;
          border-radius: var(--kc-radius-xs);
        }
      `}</style>
    </div>
  );
}
